<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import UploadBox from "@/components/modules/FileUploadModal/UploadBox.vue";
import UploadCard from "@/components/modules/FileUploadModal/UploadCard.vue";
import {useFilesStore} from "@/stores/files";
import type {FileResult, FileStatus} from "@/types/files";
import {useStore} from "@/stores";

const filesStore = useFilesStore();

const dateType = ref(true);
const isStartUpload = ref(false);
const uploadProgress = ref(0);
const isUploading = ref(false);
const uploadedBytes = ref(0);
const totalBytes = ref(0);
const isFileUploadingComplete = ref(false);

const emit = defineEmits<{
  (_e: "toggleShow"): void;
  (_e: "showUnsavedChanges"): void;
}>();

interface FileDataWithProgress {
  file_id: string;
  file_name: string;
  file_size: number;
  error: null | string;
  status?: FileStatus;
  progress?: number;
  isUploading?: boolean;
}

const fileDataList = ref<FileDataWithProgress[]>([]);

const filesWithoutErrors = computed(() => {
  return fileDataList.value.filter(file => file.error === null && file.status !== "validation_error");
});

const filesWithErrors = computed(() => {
  return fileDataList.value.filter(file => {
    return file.error || file.status === "validation_error";
  });
});

const totalSize = computed(() => {
  if (isUploading.value && totalBytes.value > 0) {
    return `${formatFileSize(uploadedBytes.value)} / ${formatFileSize(totalBytes.value)}`;
  }
  // После загрузки показываем последнее известное значение totalBytes, если оно есть
  if (totalBytes.value > 0) {
    return formatFileSize(totalBytes.value);
  }
  const bytes = fileDataList.value.reduce((sum, file) => sum + file.file_size, 0);
  return formatFileSize(bytes);
});

const isSaveDisabled = computed(() => {
  // Кнопка disabled если:
  // 1. Нет файлов в списке
  if (fileDataList.value.length === 0) return true;

  // 2. Идет процесс загрузки (глобальный или индивидуальный)
  if (isUploading.value) return true;

  // 3. Проверяем, загружается ли какой-то файл индивидуально
  if (fileDataList.value.some(file => file.isUploading === true)) return true;

  // 4. Анимация завершения загрузки еще не закончилась (600 мс)
  if (isFileUploadingComplete.value) return true;

  // 5. Начата проверка файлов.
  if (isStartFileApprove.value) return true;

  // 6. Нет файлов без ошибок.
  return filesWithoutErrors.value.length === 0;
});

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " Б";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " КБ";
  return (bytes / (1024 * 1024)).toFixed(2) + " МБ";
};

const handleRetryFile = async (fileId: string) => {
  // Создаем скрытый input для выбора файла
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".xls,.xlsx";

  input.onchange = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    // Находим файл в списке
    const fileIndex = fileDataList.value.findIndex(f => f.file_id === fileId);
    if (fileIndex === -1) {
      console.error("File not found:", fileId);
      return;
    }

    // Обновляем информацию о файле и устанавливаем статус "uploading"
    fileDataList.value[fileIndex] = {
      ...fileDataList.value[fileIndex],
      file_name: file.name,
      file_size: file.size,
      status: "uploading",
      error: null,
      progress: 0,
      isUploading: true
    };

    try {
      // Загружаем один файл с отслеживанием прогресса
      const response = await filesStore.uploadFiles([file], (progressEvent) => {
        console.log("Retry file progress:", progressEvent);

        // Обновляем прогресс для конкретного файла
        if (progressEvent.loaded !== undefined && progressEvent.total && progressEvent.total > 0) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          fileDataList.value[fileIndex] = {
            ...fileDataList.value[fileIndex],
            progress: percent
          };
        }
      });

      // Обновляем данные файла после загрузки
      if (response.file_results.length > 0) {
        const result = response.file_results[0];

        // Устанавливаем финальный статус
        fileDataList.value[fileIndex] = {
          file_id: result.file_id,
          file_name: result.file_name,
          file_size: file.size,
          error: result.error ?? null,
          status: result.status as FileStatus,
          progress: 100,
          isUploading: false
        };

        // Если загрузка успешна, показываем иконку успеха на 600 мс
        if (!result.error) {
          setTimeout(() => {
            if (fileDataList.value[fileIndex]) {
              fileDataList.value[fileIndex] = {
                ...fileDataList.value[fileIndex],
                progress: undefined,
                isUploading: undefined
              };
            }
          }, 600);
        }
      }

      console.log("File retry successful:", fileId);
    } catch (error) {
      console.error("File retry error:", error);
      // Устанавливаем статус ошибки
      fileDataList.value[fileIndex] = {
        ...fileDataList.value[fileIndex],
        status: "error",
        error: "Ошибка при повторной загрузке файла",
        progress: undefined,
        isUploading: false
      };
    }
  };

  // Открываем диалог выбора файла
  input.click();
};

const handleDeleteFile = (fileId: string) => {
  console.log("Delete file:", fileId);
  fileDataList.value = fileDataList.value.filter(f => f.file_id !== fileId);
  if (fileDataList.value.length === 0) {
    isStartUpload.value = false;
  }
};

const handleCancelFile = (fileId: string) => {
  console.log("Cancel file:", fileId);
  // Логика отмены загрузки файла
};

const handleFileUpload = async (files: FileList) => {
  const filesArray = Array.from(files);

  // Запоминаем количество существующих файлов
  const existingFilesCount = fileDataList.value.length;

  // Добавляем новые файлы к существующему списку
  fileDataList.value = [
    ...fileDataList.value,
    ...filesArray.map((file, index) => ({
      file_id: `temp-${index}-${Date.now()}`,
      file_name: file.name,
      file_size: file.size,
      error: null,
      status: "pending" as const
    }))
  ];

  isStartUpload.value = true;
  isUploading.value = true;
  uploadProgress.value = 0;
  uploadedBytes.value = 0;
  totalBytes.value = 0;

  try {
    // Устанавливаем статус "uploading" только для новых файлов
    fileDataList.value.forEach((file, index) => {
      if (index >= existingFilesCount) {
        file.status = "uploading";
      }
    });

    // Загружаем файлы с отслеживанием прогресса
    const response = await filesStore.uploadFiles(filesArray, (progressEvent) => {
      // Обновляем байты для отображения
      uploadedBytes.value = progressEvent.loaded || 0;
      totalBytes.value = progressEvent.total || 0;

      // Проверяем наличие loaded и total
      if (progressEvent.loaded !== undefined) {
        if (progressEvent.total && progressEvent.total > 0) {
          // Вычисляем процент загрузки
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        } else {
          // Если total неизвестен, показываем индикатор активности
          uploadProgress.value = Math.min(uploadProgress.value + 5, 90);
        }
      }
    });

    // Обновляем статусы файлов на основе ответа
    response.file_results.forEach((result: FileResult) => {
      const fileIndex = fileDataList.value.findIndex(f => f.file_name === result.file_name);

      // Временное отображение ошибок валидации по файлам.
      if (result.validation_result && result.validation_result.errors.length > 0) {
        console.log(`======= Ошибка валидации файла: ${result.file_name} ======`);
        for (let error of result.validation_result.errors) {
          console.log(error);
        }
        console.log("===========================================================");
      }
      // =======

      if (fileIndex !== -1) {
        fileDataList.value[fileIndex] = {
          file_id: result.file_id,
          file_name: result.file_name,
          file_size: fileDataList.value[fileIndex].file_size,
          error: result.error ?? null,
          status: result.status as FileStatus,
          progress: 0,
          isUploading: false
        };
      }
    });

  } catch (error) {
    console.error("Upload error:", error);
    // В случае ошибки устанавливаем статус "error" только для новых файлов
    fileDataList.value.forEach((file, index) => {
      if (index >= existingFilesCount) {
        file.status = "error";
      }
    });
    uploadProgress.value = 100;
  } finally {
    isUploading.value = false;

    // Показываем иконку успеха
    isFileUploadingComplete.value = true;

    // Через 600 мс скрываем иконку успеха
    setTimeout(() => {
      isFileUploadingComplete.value = false;
    }, 600);
  }
};

// Состояние проверки файлов
const isStartFileApprove = ref(false);

const handleFilesApprove = async () => {
  isStartFileApprove.value = true;
  // Собираем ID всех файлов без ошибок
  const fileIds = fileDataList.value
    .filter(file => file.error === null && file.status !== "validation_error")
    .map(file => file.file_id);

  if (fileIds.length === 0) {
    const store = useStore();
    store.showError("Ошибка подтверждения");
    console.warn("Нет файлов для подтверждения");
    return;
  }

  try {
    // Вызываем метод подтверждения файлов из store
    await filesStore.approveFiles(fileIds);

    console.log("Файлы успешно подтверждены:", fileIds);

    // Очищаем список файлов после успешного подтверждения
    fileDataList.value = [];
    isStartUpload.value = false;

    // Закрываем модальное окно после успешного подтверждения
    toggleShow();
  } catch (error) {
    console.error("Ошибка при подтверждении файлов:", error);
  }
  isStartFileApprove.value = false;
};

const toggleShow = () => {
  if (fileDataList.value.length > 0) {
    emit("showUnsavedChanges");
  } else {
    emit("toggleShow");
  }

};

// Perfect Scrollbar
const scrollContainer = ref<HTMLElement | null>(null);
let ps: PerfectScrollbar | null = null;

onMounted(() => {
  if (scrollContainer.value) {
    ps = new PerfectScrollbar(scrollContainer.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });
  }
});

onBeforeUnmount(() => {
  if (ps) {
    ps.destroy();
    ps = null;
  }
});
</script>

<template>
  <div class="upload-modal">
    <div class="upload-modal__header">
      <div class="upload-modal__header-top">
        <h1 class="upload-modal__title">
          Загрузка данных
        </h1>
        <button
          class="upload-modal__button-close"
          @click="toggleShow"
        >
          <IconCross class="upload-modal__icon-close" />
        </button>
      </div>
      <span class="upload-modal__subtitle">Для начала работы выберите тип загружаемых данных <br> и файлы для загрузки</span>
    </div>

    <div
      ref="scrollContainer"
      class="upload-modal__content"
    >
      <div class="upload-step">
        <h3 class="upload-step__title">
          Шаг 1. Выберите тип данных
        </h3>
        <div class="upload-step__radio-group">
          <RadioButton
            v-model="dateType"
            :highlight-active-label="false"
            :value="true"
            label="Официальные данные"
          />
          <RadioButton
            v-model="dateType"
            :highlight-active-label="false"
            :value="false"
            label="Оперативные данные"
            disabled=""
          />
        </div>
      </div>

      <div class="upload-step">
        <h3 class="upload-step__title">
          Шаг 2. Выберите и загрузите файлы
        </h3>

        <UploadBox
          accepted-formats="XLS"
          max-size="25 Мб"
          @upload="handleFileUpload"
        />
        <!--   Карточка файлов без ошибок     -->
        <UploadCard
          v-if="isStartUpload && filesWithoutErrors.length > 0"
          :files="filesWithoutErrors"
          :total-size="totalSize"
          status-text="Загрузка завершена"
          :progress="uploadProgress"
          :is-uploading="isUploading"
          :is-file-uploading-complete="isFileUploadingComplete"
          @retry-file="handleRetryFile"
          @delete-file="handleDeleteFile"
          @cancel-file="handleCancelFile"
        />
        <!--   Карточка файлов с ошибками     -->
        <UploadCard
          v-if="isStartUpload && filesWithErrors.length > 0"
          :files="filesWithErrors"
          :total-size="totalSize"
          status-text="Ошибка"
          :is-error="true"
          :progress="uploadProgress"
          :is-uploading="isUploading"
          :is-file-uploading-complete="isFileUploadingComplete"
          @retry-file="handleRetryFile"
          @delete-file="handleDeleteFile"
          @cancel-file="handleCancelFile"
        />
      </div>
    </div>

    <div class="upload-modal__footer">
      <ButtonAccentSecondary
        size="large"
        @click="toggleShow"
      >
        Отмена
      </ButtonAccentSecondary>
      <ButtonAccentPrimary
        :disabled="isSaveDisabled"
        size="large"
        @click="handleFilesApprove"
      >
        Сохранить
      </ButtonAccentPrimary>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-modal {
  position: fixed;
  right: 20px;
  top: 20px;
  height: calc(100% - 40px);
  background-color: var(--background-bg-white);
  border-radius: 10px;
  padding: 20px 24px 24px 24px;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  display: flex;
  flex-direction: column;
  gap: 24px;
  z-index: 100;

  min-width: 576px;

  &__content {
    position: relative;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 24px;

    &.ps--active-y {
      padding-right: 15px;
    }
  }

  &__footer {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    margin-top: auto;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__header-top {
    display: flex;
    justify-content: space-between;
  }

  &__title {
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: var(--text-text-black);
  }

  &__subtitle {
    color: var(--text-text-gray);
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }

  &__icon-close {
    width: 20px;
    height: 20px;
  }

  &__button-close {
    appearance: none;
    background-color: transparent;
    cursor: pointer;
    user-select: none;
    color: var(--icon-icon-secondary);
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      color: var(--icon-icon-on);
    }
  }
}

.upload-step {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }

  &__radio-group {
    display: flex;
    flex-direction: column;
    gap: 8px;

    :deep(.radio-button__label) {
      font-weight: 500;
    }
  }
}

:deep(.ps__rail-y) {
  background-color: #FFFFFF !important;
  opacity: 1 !important;
  width: 7px !important;
  right: 0;
}

:deep(.ps__thumb-y) {
  background-color: #EBEBEB !important;
  border-radius: 30px !important;
  width: 7px !important;
}
</style>